<template>
  <div class="record-browser">
    <div class="header">
      <h3>Records<HelpLink topic="records" title="What are records?" /></h3>

      <div class="header-buttons">
        <button @click="navigateToCreateRecord" class="new-record-button">
          + Record
        </button>
        <button @click="showCreateTypeDialog = true" class="new-type-button">
          + Record Type
        </button>
      </div>
    </div>

    <div class="search-filter-row">
      <input
        type="text"
        v-model="searchQuery"
        placeholder="Search by Name..."
        class="search-input"
      />
      <button @click="showFilters = !showFilters" class="toggle-filter-button">
        {{ showFilters ? 'Hide Filters' : 'Show Filters' }}
      </button>
    </div>

    <!-- Collapsible Filter Menu -->
    <div v-if="showFilters" class="filters">
      <select v-model="typeFilter" class="select-input">
        <option value="">All Types</option>
        <option v-for="type in recordTypes" :key="type.id" :value="type.id.toString()">
          {{ type.name }}
        </option>
      </select>
    </div>

    <table class="record-table">
      <thead>
        <tr>
          <th @click="sortItems('name')">
            Name
            <i
              v-if="currentSortColumn === 'name'"
              class="filter-arrow"
              :class="setArrowDirection(currentSortDirection)"
            ></i>
          </th>

          <th @click="sortItems('type')">
            Type
            <i
              v-if="currentSortColumn === 'type'"
              class="filter-arrow"
              :class="setArrowDirection(currentSortDirection)"
            ></i>
          </th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="item in paginatedItems"
          :key="item.rowKey"
          @click="navigateToDetail(item)"
          class="record-row"
        >
          <td>{{ item.name }}</td>
          <td>{{ item.type || '—' }}</td>
        </tr>
      </tbody>
    </table>

    <div class="pagination">
      <button
        v-for="page in visiblePageNumbers"
        :key="page"
        @click="setPage(page)"
        :class="{ active: page === currentPage }"
      >
        {{ page }}
      </button>
    </div>

    <!-- Create Record Type Dialog -->
    <div v-if="showCreateTypeDialog" class="modal-overlay" @click.self="showCreateTypeDialog = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Create New Record Type</h3>
          <button type="button" class="close-btn" @click="showCreateTypeDialog = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label for="type-name">Name</label>
            <input
              type="text"
              id="type-name"
              v-model="newRecordType.name"
              class="form-control"
              placeholder="e.g., Sample, Analysis, etc."
            />
          </div>
          <div class="form-group">
            <label for="type-desc">Description (optional)</label>
            <textarea
              id="type-desc"
              v-model="newRecordType.description"
              class="form-control"
              placeholder="Describe this record type..."
              rows="4"
            ></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="cancel-btn" @click="showCreateTypeDialog = false">Cancel</button>
          <button type="button" class="save-btn" @click="createRecordType" :disabled="creatingType">
            {{ creatingType ? 'Creating...' : 'Create' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_ENDPOINTS } from "@/services/config.js";
import apiService from "@/services/apiService";
import { useToast } from "vue-toastification";
import { mapState } from "vuex";

export default {
  data() {
    return {
      records: [],
      recordTypes: [],
      recordTypeMap: {}, // Map record_type_id to name for quick lookup

      searchQuery: "",
      showFilters: false,
      typeFilter: "",

      currentPage: 1,
      itemsPerPage: 15,

      currentSortColumn: "name",
      currentSortDirection: "asc",

      sortedItems: [],

      showCreateTypeDialog: false,
      newRecordType: {
        name: "",
        description: "",
      },
      creatingType: false,
    };
  },

  computed: {
    ...mapState(["loggedInUser"]),

    filteredItems() {
      const q = this.searchQuery.toLowerCase().trim();
      const typeIdFilter = this.typeFilter ? parseInt(this.typeFilter) : null;

      return this.sortedItems.filter((item) => {
        const name = item.name?.toLowerCase() || "";
        const matchesSearch = q === "" || name.includes(q);

        const matchesType =
          typeIdFilter === null ||
          item.record_type_id === typeIdFilter;

        return matchesSearch && matchesType;
      });
    },

    paginatedItems() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredItems.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredItems.length / this.itemsPerPage);
    },

    visiblePageNumbers() {
      const range = 15;
      let startPage = Math.max(1, this.currentPage - Math.floor(range / 2));
      let endPage = startPage + range - 1;

      if (endPage > this.totalPages) {
        endPage = this.totalPages;
        startPage = Math.max(1, endPage - range + 1);
      }

      return Array.from(
        { length: Math.max(0, endPage - startPage + 1) },
        (_, i) => startPage + i
      );
    },
  },

  setup() {
    const toast = useToast();
    return { toast };
  },

  created() {
    this.fetchRecordTypes();
    this.fetchAllRecords();
  },

  methods: {
    async fetchRecordTypes() {
      try {
        const response = await apiService.fetchResources(
          API_ENDPOINTS.recordTypes,
          this.loggedInUser?.entra_id
        );
        this.recordTypes = Array.isArray(response) ? response : [];
        
        // Build quick lookup map
        this.recordTypeMap = {};
        this.recordTypes.forEach(type => {
          this.recordTypeMap[type.id] = type.name;
        });
      } catch (error) {
        console.error("Error fetching record types:", error);
      }
    },

    async fetchAllRecords() {
      try {
        const response = await axios.get(API_ENDPOINTS.allRecords, {
          params: { entra_id: this.loggedInUser?.entra_id },
        });
        const records = Array.isArray(response.data) ? response.data : [];

        // Build a unified list for the table
        const unified = records.map((r) => ({
          rowKey: `record-${r.node_id}`,
          id: r.node_id,
          name: r.name,
          description: null,
          user_id: r.user_id,
          record_type_id: r.record_type_id,
          kind: "record",
          kindLabel: "Record",
          kindSort: 1,
          type: this.recordTypeMap[r.record_type_id] || `Type ${r.record_type_id}`,
        }));

        this.sortedItems = unified;
        this.sortItems(this.currentSortColumn);
      } catch (error) {
        console.error("Error fetching /all-records:", error);
      }
    },

    async createRecordType() {
      if (!this.newRecordType.name.trim()) {
        this.toast.error("Record type name is required");
        return;
      }

      this.creatingType = true;
      try {
        const payload = {
          name: this.newRecordType.name,
          description: this.newRecordType.description,
        };

        const response = await apiService.createResource(
          API_ENDPOINTS.recordTypes,
          payload,
          this.loggedInUser.entra_id
        );

        this.recordTypes.push(response);
        this.recordTypeMap[response.id] = response.name;
        this.showCreateTypeDialog = false;
        this.newRecordType = { name: "", description: "" };
        this.toast.success("Record type created successfully!");
      } catch (error) {
        console.error("Failed to create record type:", error);
        this.toast.error("Failed to create record type!");
      } finally {
        this.creatingType = false;
      }
    },

    sortItems(column) {
      if (this.currentSortColumn === column) {
        this.currentSortDirection =
          this.currentSortDirection === "asc" ? "desc" : "asc";
      } else {
        this.currentSortDirection = "asc";
        this.currentSortColumn = column;
      }

      const dir = this.currentSortDirection === "asc" ? 1 : -1;

      this.sortedItems.sort((a, b) => {
        const av = a[column];
        const bv = b[column];

        // Null-safe comparison
        if (av == null && bv == null) return 0;
        if (av == null) return 1 * dir;
        if (bv == null) return -1 * dir;

        // Strings: case-insensitive
        if (typeof av === "string" && typeof bv === "string") {
          return av.localeCompare(bv, undefined, { sensitivity: "base" }) * dir;
        }

        // Numbers / fallback
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });

      // If you sort while on page > 1, it’s usually nicer to go back to page 1
      this.currentPage = 1;
    },

    setPage(pageNumber) {
      this.currentPage = pageNumber;
    },

    setArrowDirection(sortDirection) {
      return sortDirection === "desc" ? "bi bi-arrow-down" : "bi bi-arrow-up";
    },

    navigateToDetail(item) {
      // Navigate to RecordDetailPage
      this.$router.push({ name: "RecordDetailPage", params: { id: item.id } });
    },

    
    navigateToCreateRecord() {
        this.$router.push({ path: "/create-record" });
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.record-browser {
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
  background: $secondary-color;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h3 {
      color: $primary-color;
    }

    .header-buttons {
      display: flex;
      gap: 10px;
    }

    .new-record-button,
    .new-type-button {
      padding: 8px 16px;
      background-color: $primary-color;
      color: #fff;
      border: none;
      border-radius: 5px;
      cursor: pointer;
    }

    .new-record-button:hover,
    .new-type-button:hover {
      background-color: darken($primary-color, 10%);
    }
  }

  .search-filter-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 8px 0;
  }

  .search-input {
    flex: 1;
    margin: 0;
  }

  .toggle-filter-button {
    width: auto;
    margin: 0;
    padding: 6px 10px;
    white-space: nowrap;
  }

  .record-table {
    width: 100%;
  }

  .record-table th {
    background-color: $form-color;
    border: 1px solid $primary-color;
    cursor: pointer;
  }

  .filters {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
    max-width: 1000px;
    margin: 6px 0;

    .select-input,
    .text-input {
      flex: 1;
      padding: 8px;
      border-radius: 5px;
      border: 1px solid $primary-color;
      background: $secondary-color;
    }
  }

  .record-row {
    background-color: $secondary-color;
    cursor: pointer;
  }

  .record-row:hover {
    background-color: $form-color;
  }

  .pagination {
    display: flex;
    justify-content: center;
    margin-top: 20px;
  }

  .pagination button {
    margin: 0 5px;
    padding: 5px 10px;
    background-color: $primary-color;
    border: 1px solid $secondary-color;
    border-radius: 5px;
    cursor: pointer;
  }

  .pagination button:hover,
  .pagination button.active {
    background-color: darken($primary-color, 10%);
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;

  .modal-content {
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
    max-width: 500px;
    width: 90%;
    max-height: 80vh;
    overflow-y: auto;

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px;
      border-bottom: 1px solid #e0e0e0;

      h3 {
        margin: 0;
        color: $primary-color;
      }

      .close-btn {
        background: none;
        border: none;
        font-size: 24px;
        cursor: pointer;
        color: #666;

        &:hover {
          color: #000;
        }
      }
    }

    .modal-body {
      padding: 20px;

      .form-group {
        margin-bottom: 15px;

        label {
          display: block;
          margin-bottom: 5px;
          font-weight: 500;
          color: #333;
        }

        input,
        textarea {
          width: 100%;
          padding: 8px;
          border: 1px solid #ccc;
          border-radius: 4px;
          font-family: inherit;

          &:focus {
            outline: none;
            border-color: $primary-color;
          }
        }
      }
    }

    .modal-footer {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      padding: 20px;
      border-top: 1px solid #e0e0e0;

      button {
        padding: 8px 16px;
        border-radius: 4px;
        border: none;
        cursor: pointer;
        font-size: 14px;

        &.cancel-btn {
          background-color: #e0e0e0;
          color: #333;

          &:hover {
            background-color: #d0d0d0;
          }
        }

        &.save-btn {
          background-color: $primary-color;
          color: white;

          &:hover {
            background-color: darken($primary-color, 10%);
          }

          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        }
      }
    }
  }
}
</style>